/**
 * Confidence Scoring for Simple Whole-House Heat Loss
 *
 * Computes a data-completeness confidence score (0–100) based on how many
 * inputs the user explicitly provided vs how many were inferred from defaults.
 */

import type { SimpleHeatLossInputs } from './types';

/**
 * Confidence interpretation band.
 */
export type ConfidenceBand = 'High' | 'Medium' | 'Low';

/**
 * Compute the confidence score (0–100) for a set of SimpleHeatLossInputs.
 *
 * Scoring:
 *   Base                    30
 *   + floorAreaM2 provided  15  (always true — it is required)
 *   + ceilingHeight set     10  (non-default value treated as measured)
 *   + insulationLevel expl  15
 *   + glazingType expl      10
 *   + airTightness expl     10
 *   + exposedSides expl     10
 *   + extension area given  10  (when extensionPlanned is true)
 *   Capped at 100.
 */
export function calculateSimpleConfidence(inputs: SimpleHeatLossInputs): number {
  let score = 30;

  // Floor area is always required
  if (inputs.floorAreaM2 > 0) score += 15;

  // Ceiling height: if user explicitly set a non-default value we treat it as measured
  if (inputs.ceilingHeightM !== 2.4) score += 10;

  if (inputs.insulationLevelExplicit) score += 15;
  if (inputs.glazingTypeExplicit)     score += 10;
  if (inputs.airTightnessExplicit)    score += 10;
  if (inputs.exposedSidesExplicit)    score += 10;

  if (inputs.extensionPlanned && inputs.extensionAreaM2 != null && inputs.extensionAreaM2 > 0) {
    score += 10;
  }

  return Math.min(100, score);
}

/**
 * Map a 0–100 confidence score to a human-readable band label.
 */
export function confidenceBand(score: number): ConfidenceBand {
  if (score >= 80) return 'High';
  if (score >= 60) return 'Medium';
  return 'Low';
}

/**
 * Warning message shown when confidence is below 60.
 */
export const LOW_CONFIDENCE_WARNING =
  'Sizing confidence low — verify before final specification.';
